/**
 * features/auth/api.ts — typed wrappers around the /api/auth/* endpoints.
 * Every call validates the response against a zod schema at the boundary
 * (frontend-conventions.md §5).
 */

import { z } from 'zod';
import { api } from '@/lib/api';
import {
  LoginResponseSchema,
  MeResponseSchema,
  ResendVerificationResponseSchema,
  SignupResponseSchema,
  VerifyEmailResponseSchema,
  type LoginFormValues,
  type LoginResponse,
  type MeResponse,
  type ResendVerificationResponse,
  type SignupFormValues,
  type SignupResponse,
  type VerifyEmailResponse,
} from './schemas';

export async function signup(values: SignupFormValues): Promise<SignupResponse> {
  return api.post('/auth/signup', SignupResponseSchema, values);
}

export async function login(values: LoginFormValues): Promise<LoginResponse> {
  return api.post('/auth/login', LoginResponseSchema, values);
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout', z.void(), undefined);
}

export async function me(tenantId?: string): Promise<MeResponse> {
  return api.get('/auth/me', MeResponseSchema, {
    headers: tenantId ? { 'X-Tenant-Id': tenantId } : undefined,
  });
}

export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  return api.post('/auth/verify-email', VerifyEmailResponseSchema, { token });
}

export async function resendVerification(email: string): Promise<ResendVerificationResponse> {
  return api.post('/auth/resend-verification', ResendVerificationResponseSchema, { email });
}
